import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { displayDate } from '../common/datePresentation';
import { groupScheduleLessons, isScheduleDate, scheduleWindowEnd, shiftScheduleWindow } from './clientScheduleWindow';

export default function ClientSchedule({ lessons, window, today, studentLabel, disabled, onWindowChange, renderLesson }) {
  const { t, i18n } = useTranslation();
  const groups = groupScheduleLessons(lessons, window);
  const count = groups.reduce((total, group) => total + group.lessons.length, 0);
  const rangeLabel = `${displayDate(window.start, i18n.language)}–${displayDate(scheduleWindowEnd(window), i18n.language)}`;
  const weekday = date => new Intl.DateTimeFormat(i18n.language === 'en' ? 'en-GB' : 'pl-PL', { weekday: 'long', timeZone: 'Europe/Warsaw' }).format(new Date(`${date}T12:00:00Z`));
  return (
    <div className="client-portal__schedule">
      <section className="client-portal__card client-portal__schedule-toolbar" aria-label={t('client:schedule.navigation')}>
        <div className="client-portal__schedule-controls">
          <label htmlFor="schedule-days">{t('client:schedule.show')}
            <select id="schedule-days" className="form-control" value={window.days} disabled={disabled} onChange={event => onWindowChange({ ...window, days: Number(event.target.value) })}>
              <option value={7}>{t('client:schedule.week')}</option><option value={14}>{t('client:schedule.fortnight')}</option>
            </select>
          </label>
          <div className="client-portal__schedule-navigation">
            <button type="button" className="btn btn-outline-secondary" disabled={disabled || window.start <= today} onClick={() => onWindowChange(shiftScheduleWindow(window, -1, today))}>{t('client:schedule.previous')}</button>
            <button type="button" className="btn btn-outline-secondary" disabled={disabled} onClick={() => onWindowChange({ ...window, start: today })}>{t('client:schedule.nearest')}</button>
            <button type="button" className="btn btn-outline-secondary" disabled={disabled} onClick={() => onWindowChange(shiftScheduleWindow(window, 1, today))}>{t('client:schedule.next')}</button>
          </div>
        </div>
        <p className="client-portal__schedule-range" aria-live="polite">{rangeLabel}</p>
        <div className="client-portal__schedule-scope"><span>{t('client:schedule.count', { count })}</span><span>{t('client:schedule.timezone')}</span></div>
      </section>
      {lessons.some(lesson => !isScheduleDate(lesson.date)) && <p className="alert alert-warning" role="status">{t('client:schedule.invalidDates')}</p>}
      {groups.map(group => (
        <section className="client-portal__schedule-day" key={group.date} aria-label={`${weekday(group.date)} · ${displayDate(group.date, i18n.language)}`}>
          <h2>{weekday(group.date)} · {displayDate(group.date, i18n.language)}</h2>
          {group.lessons.map(lesson => renderLesson(lesson, { variant: 'schedule', showDate: false }))}
        </section>
      ))}
      {!groups.length && <section className="client-portal__card client-portal__schedule-empty"><h2>{t('client:schedule.empty')}</h2><p>{studentLabel}</p><p>{rangeLabel}</p><p>{t('client:schedule.changeFilters')}</p></section>}
    </div>
  );
}
ClientSchedule.propTypes = {
  lessons: PropTypes.array.isRequired,
  window: PropTypes.shape({ start: PropTypes.string.isRequired, days: PropTypes.number.isRequired }).isRequired,
  today: PropTypes.string.isRequired,
  studentLabel: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  onWindowChange: PropTypes.func.isRequired,
  renderLesson: PropTypes.func.isRequired,
};
ClientSchedule.defaultProps = { disabled: false };
